/**
 * Trainer line-of-sight detection.
 *
 * Pokemon-style: each placed trainer scans a straight line in the direction
 * they face, up to `sightRange` tiles. If the player stands on one of those
 * tiles, the trainer locks on, an exclamation bubble flashes for a beat, and
 * the trainer auto-walks up to one tile away to start the battle.
 *
 * Defeated trainers are remembered internally so a re-entry into their line
 * of sight is a no-op.
 */

export type TrainerFacing = "down" | "left" | "right" | "up";

export type TrainerPlacement = {
  trainerId: string;
  x: number;
  y: number;
  facing: TrainerFacing;
  sightRange: number;
};

export type DetectionResult = {
  trainerId: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  walkPath: Array<{ x: number; y: number }>;
};

type Vec2 = { dx: number; dy: number };

const FACING_DELTA: Record<TrainerFacing, Vec2> = {
  down: { dx: 0, dy: 1 },
  up: { dx: 0, dy: -1 },
  left: { dx: -1, dy: 0 },
  right: { dx: 1, dy: 0 },
};

const NOTIFICATION_DURATION_MS = 600;

export class TrainerDetectionSystem {
  private readonly defeated: Set<string>;
  private placements: TrainerPlacement[];
  private notification: { trainerId: string; expiresAt: number } | null;

  constructor(opts?: { defeatedTrainerIds?: string[] }) {
    this.defeated = new Set(opts?.defeatedTrainerIds ?? []);
    this.placements = [];
    this.notification = null;
  }

  setActiveTrainers(placements: TrainerPlacement[]): void {
    // Copy to detach from caller's array mutations.
    this.placements = placements.map((p) => ({ ...p }));
  }

  markDefeated(trainerId: string): void {
    this.defeated.add(trainerId);
    if (this.notification?.trainerId === trainerId) {
      this.notification = null;
    }
  }

  isDefeated(trainerId: string): boolean {
    return this.defeated.has(trainerId);
  }

  checkDetection(
    playerX: number,
    playerY: number,
    isBlocked: (x: number, y: number) => boolean,
  ): DetectionResult | null {
    let best: { result: DetectionResult; distance: number } | null = null;

    for (const placement of this.placements) {
      if (this.defeated.has(placement.trainerId)) continue;

      const result = this.scanLine(placement, playerX, playerY, isBlocked);
      if (!result) continue;

      const distance = result.walkPath.length + 1; // tiles from trainer to player
      if (best === null || distance < best.distance) {
        best = { result, distance };
      }
    }

    if (best) {
      this.notification = {
        trainerId: best.result.trainerId,
        expiresAt: Date.now() + NOTIFICATION_DURATION_MS,
      };
      return best.result;
    }

    return null;
  }

  /**
   * Returns the trainer id currently showing an '!' bubble, or null.
   * Bubble auto-expires after NOTIFICATION_DURATION_MS.
   */
  getActiveNotification(): { trainerId: string; remainingMs: number } | null {
    if (!this.notification) return null;
    const remainingMs = this.notification.expiresAt - Date.now();
    if (remainingMs <= 0) {
      this.notification = null;
      return null;
    }
    return { trainerId: this.notification.trainerId, remainingMs };
  }

  clearNotification(): void {
    this.notification = null;
  }

  reset(): void {
    this.placements = [];
    this.notification = null;
    this.defeated.clear();
  }

  private scanLine(
    placement: TrainerPlacement,
    playerX: number,
    playerY: number,
    isBlocked: (x: number, y: number) => boolean,
  ): DetectionResult | null {
    const { dx, dy } = FACING_DELTA[placement.facing];
    const walkPath: Array<{ x: number; y: number }> = [];

    for (let step = 1; step <= placement.sightRange; step++) {
      const tileX = placement.x + dx * step;
      const tileY = placement.y + dy * step;

      if (tileX === playerX && tileY === playerY) {
        // Player spotted. walkPath stops one tile short of the player.
        return {
          trainerId: placement.trainerId,
          fromX: placement.x,
          fromY: placement.y,
          toX: playerX,
          toY: playerY,
          walkPath,
        };
      }

      if (isBlocked(tileX, tileY)) {
        // Sight line obstructed before reaching the player.
        return null;
      }

      walkPath.push({ x: tileX, y: tileY });
    }

    return null;
  }
}
